import { useEffect, useState } from 'react';
import { Lock, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { getSiteAccessSettings, updateSiteAccessSettings } from 'wasp/client/operations';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

const SiteAccessTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [envOverrideActive, setEnvOverrideActive] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await getSiteAccessSettings();
      setEnabled(settings.enabled && settings.managedInDatabase);
      setHasPassword(settings.hasPassword);
      setEnvOverrideActive(settings.envOverrideActive);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load site access settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (enabled && password) {
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }

    if (enabled && !hasPassword && !password.trim()) {
      toast.error('Enter a password before enabling site access protection');
      return;
    }

    try {
      setSaving(true);
      await updateSiteAccessSettings({
        enabled,
        password: password.trim() || undefined,
      });
      toast.success(
        enabled
          ? 'Site access protection enabled'
          : 'Site access protection disabled'
      );
      setPassword('');
      setConfirmPassword('');
      await loadSettings();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update site access settings');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggle = async (newEnabled: boolean) => {
    if (newEnabled && !hasPassword && !password.trim()) {
      setEnabled(true);
      toast.error('Set a password below, then save to enable protection');
      return;
    }

    const previousEnabled = enabled;
    setEnabled(newEnabled);

    try {
      setSaving(true);
      await updateSiteAccessSettings({
        enabled: newEnabled,
        password: password.trim() || undefined,
      });
      toast.success(
        newEnabled
          ? 'Site access protection enabled'
          : 'Site access protection disabled'
      );
      setPassword('');
      setConfirmPassword('');
      await loadSettings();
    } catch (error: any) {
      setEnabled(previousEnabled);
      toast.error(error.message || 'Failed to toggle site access protection');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">Loading site access settings...</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="px-6 py-6 space-y-6">
        {envOverrideActive && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Environment override active</AlertTitle>
            <AlertDescription>
              Site access is enabled via the <code className="text-xs">SITE_ACCESS_PASSWORD</code>{' '}
              environment variable. Remove it from your server environment to control protection
              from this page.
            </AlertDescription>
          </Alert>
        )}

        {enabled && !envOverrideActive && (
          <Alert>
            <Lock className="h-4 w-4" />
            <AlertTitle>Site access protection is active</AlertTitle>
            <AlertDescription>
              Visitors must enter the access password before they can browse the site. The gate
              applies to all routes until unlocked for the browser session.
            </AlertDescription>
          </Alert>
        )}

        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              <h3 className="font-semibold leading-none tracking-tight">Site Access Protection</h3>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={handleQuickToggle}
              disabled={saving || envOverrideActive}
              className="ml-auto"
            />
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">
            Require a shared password before anyone can use the platform (staging / pre-launch)
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="font-semibold leading-none tracking-tight">Access password</h3>
            <p className="text-sm text-muted-foreground mt-1.5">
              {hasPassword
                ? 'Leave blank to keep the current password, or enter a new one to change it.'
                : 'Required when enabling protection.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="site-access-new-password">Password</Label>
              <Input
                id="site-access-new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={saving || envOverrideActive}
                placeholder={hasPassword ? 'New password (optional)' : 'Access password'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-access-confirm-password">Confirm password</Label>
              <Input
                id="site-access-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={saving || envOverrideActive}
                placeholder="Confirm password"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" onClick={loadSettings} disabled={saving}>
              Reset
            </Button>
            <Button onClick={handleSave} disabled={saving || envOverrideActive}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">How it works</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                When enabled, visitors land on the site access page and must enter the password
                once per browser session.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                Disabling protection removes the gate immediately; the stored password is kept so
                you can re-enable without setting it again.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                Passwords are stored hashed and are never shown again after saving.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SiteAccessTab;
